import type { FunnelStep, FunnelSnapshotResponse, MeterDetail } from '@rheora/enrollment-contract';

import { DECLARED_MONTHLY_KWH_MAX } from '@rheora/enrollment-contract';
import { CUSTOM_TIER_ID, USAGE_TIERS, type ConsumptionSelectionId } from './consumption';

/**
 * How a RETURNING TAB reconstructs the funnel — pure, no React, no I/O.
 *
 * Refreshing the browser mid-funnel used to lose everything. Two different fixes are
 * needed, because the state lives in two different places, and this module owns the
 * decision logic for both so no `.tsx` has to:
 *
 *  1. THE SERVER-HELD STATE (the resolved meter + its opaque handle) comes back from
 *     `GET /bff/funnel`. It was always there — `session/store.ts` has persisted it
 *     since E-SF01 — there was simply no endpoint to read it.
 *
 *  2. THE PURELY-CLIENT STATE (monthly usage + which tier produced it) is NOT ON THE
 *     SERVER AT ALL: no funnel-session key holds it, because it is display state that
 *     only ever fed the shelf's pricing. Rather than invent a server field for it,
 *     it rides the URL — which survives a refresh AND makes a priced shelf
 *     BOOKMARKABLE and shareable, neither of which a server field would give.
 *
 *     ⚠️ THE URL IS THE SINGLE SOURCE OF TRUTH FOR USAGE, AND EVERY FUNNEL LINK
 *     CARRIES IT. `FunnelProvider` is the read API (seeded from here on mount);
 *     components read `useFunnel().monthlyUsageKwh` and never parse the URL
 *     themselves. The writers are the four builders below — `buildShelfPath`,
 *     `buildPlanDetailPath`, `buildPlanStepPath` and the generic
 *     `withUsageSelection` — and nothing else may assemble one of these paths.
 *     When only the shelf CTA wrote the params, the shopper's 1400 kWh survived
 *     exactly one hop: the plan detail re-defaulted to the 1000 kWh EFL midpoint
 *     and a refresh anywhere past the shelf bounced them back to the chooser.
 *
 *     Storage (`@fluxora/storage`) is deliberately NOT used for this: it would
 *     resurrect a stale usage on a fresh deep link, where "no usage in the URL"
 *     genuinely means "this visitor has not chosen one".
 *
 * ── WHY `method` IS DELIBERATELY *NOT* RESTORED ────────────────────────────
 * A recorded product decision (2026-07-28, `funnelMethod.ts`) is that the landing
 * chooser's method persistence is IN-MEMORY ONLY — "no URL param, no storage" — so
 * a cold load defaults to "Shop by ZIP". Putting it in the URL here would reverse a
 * ratified decision as a side effect of a bug fix. It also buys nothing: once a meter
 * IS resolved the chooser is replaced by the resolved-meter panel, so which tab got
 * you there is unobservable; and with no meter resolved, the documented default is
 * the right answer anyway.
 */

/** `?usage=` — the chosen MONTHLY kWh the shelf prices plans at. */
export const USAGE_PARAM = 'usage';
/** `?tier=` — which consumption choice produced it ('low'|'medium'|'high'|'custom'). */
export const TIER_PARAM = 'tier';

/**
 * The subset of funnel state a returning tab can legitimately restore.
 *
 * Every field is optional: a first-time visitor restores nothing, and that is a
 * valid seed rather than an error. Declared here (not as `Partial<FunnelState>`) so
 * the import runs state → services like every other funnel service, and so the
 * SHAPE of what may be rehydrated is stated in one reviewable place.
 *
 * ⚠️ NOTE WHAT CANNOT BE IN HERE, EVER: the SSN, the date of birth, the customer's
 * name/phone, and the in-flight OTP code. The SSN is never persisted anywhere by
 * anyone — it is EXPECTED to vanish on refresh, and that is the promise the credit
 * page makes the customer.
 */
export interface FunnelSeed {
  /**
   * The furthest step the SERVER can evidence (E-SF19 P1 gating).
   *
   * ⚠️ THIS IS THE SERVER'S ANSWER, NOT THE SPA'S ROUTE. It is derived from what
   * the funnel session actually contains, so a client that lies about its progress
   * cannot use it to unlock a step. `undefined` means the rehydrate read did not
   * settle with a snapshot (a 401, or a failure) — which is the absence of
   * evidence, not evidence of absence, and `funnelGateFor` deliberately does not
   * gate on it.
   */
  readonly serverStep?: FunnelStep;
  readonly candidateId?: string;
  readonly meterDetail?: MeterDetail;
  readonly zip?: string;
  readonly monthlyUsageKwh?: number;
  readonly usageTier?: ConsumptionSelectionId;
  /**
   * True when the SERVER session already holds the step-one lead contact
   * (E-CRM02). The landing page shows the contact form FIRST on every fresh
   * or rehydrated visit until this is true — a resolved meter alone must not
   * skip the step, or a reload walks straight past lead capture.
   */
  readonly hasLeadContact?: boolean;
  /**
   * E-CRM04 — the BFF rebuilt this session from an emailed "resume your
   * enrollment" link. A render hint only (the welcome-back banner); it unlocks
   * nothing. Taken even when `meterOnly`: it is a fact about how the session was
   * made, not a step that can go stale.
   */
  readonly resumed?: boolean;
  /**
   * E-CRM04 — what the resume link restored: `full` or `progress` (plan and
   * usage only — the email was never proven). Render hint for the banner copy.
   */
  readonly resumeScope?: 'full' | 'progress';
}

/** True when `value` is one of the four consumption choice tokens. */
export function isConsumptionSelectionId(value: string): value is ConsumptionSelectionId {
  return value === CUSTOM_TIER_ID || USAGE_TIERS.some((tier) => tier.id === value);
}

/**
 * Parse a positive-integer monthly kWh out of a raw query-param value.
 *
 * Strict on purpose: a URL is attacker-supplied text, and `Number('12e9')`,
 * `Number(' 12 ')` and `Number('12.5')` all produce a "number" that would then be
 * rendered as the customer's usage and used to price plans. Only bare digits count,
 * and only a positive value (mirrors `parseCustomKwh` in `consumption.ts`, which
 * owns the same rule for typed input).
 *
 * @returns the kWh, or null when the value is absent or not a positive integer.
 */
export function parseUsageParam(raw: string | null): number | null {
  if (raw === null) return null;
  if (!/^\d+$/u.test(raw)) return null;
  const value = Number.parseInt(raw, 10);
  // Monthly kWh, so bounded like the declared figure: a value above the
  // ceiling is not a usage this shelf can price (an annual figure once
  // arrived here and was labelled "12000 kWh/mo").
  return value > 0 && value <= DECLARED_MONTHLY_KWH_MAX ? value : null;
}

export interface UsageSelection {
  readonly monthlyUsageKwh?: number;
  readonly usageTier?: ConsumptionSelectionId;
}

/**
 * The usage selection carried by the URL, or an empty object.
 *
 * ⚠️ THE kWh IS THE GATE, NOT THE TIER. `MeterPlansPage` guards on
 * `monthlyUsageKwh`, so a URL with `?tier=medium` and no (or a junk) `?usage=`
 * restores NOTHING — including the tier. Restoring a lone tier would light up the
 * radio button while the shelf still bounced the shopper back to the chooser, which
 * is a worse outcome than restoring nothing at all.
 */
export function parseUsageSelection(params: URLSearchParams): UsageSelection {
  const monthlyUsageKwh = parseUsageParam(params.get(USAGE_PARAM));
  if (monthlyUsageKwh === null) return {};

  const rawTier = params.get(TIER_PARAM);
  const usageTier = rawTier !== null && isConsumptionSelectionId(rawTier) ? rawTier : undefined;

  return {
    monthlyUsageKwh,
    ...(usageTier === undefined ? {} : { usageTier }),
  };
}

/**
 * The `?usage=`/`?tier=` pair for a selection — EMPTY when there is no valid kWh.
 *
 * An incomplete `?usage=` would restore nothing on arrival (see
 * `parseUsageSelection`), so writing one is just noise in the address bar. Every
 * path builder below goes through this, which is what makes "the URL is the single
 * source of truth for usage" a property of ONE function rather than a convention
 * each call site has to remember.
 */
export function usageSearchParams(selection: UsageSelection): URLSearchParams {
  const { monthlyUsageKwh, usageTier } = selection;
  if (monthlyUsageKwh === undefined || !Number.isInteger(monthlyUsageKwh) || monthlyUsageKwh <= 0) {
    return new URLSearchParams();
  }

  const params = new URLSearchParams({ [USAGE_PARAM]: String(monthlyUsageKwh) });
  if (usageTier !== undefined) params.set(TIER_PARAM, usageTier);
  return params;
}

/**
 * `path` with the usage selection MERGED into its query string.
 *
 * ⚠️ MERGED, NOT REPLACED. Several funnel links already carry the back-compat
 * `?candidate=<id>`; clobbering it would drop the shopper's resolved meter on the
 * very hop that was meant to preserve their usage. Foreign params are kept as they
 * are; the usage PAIR is owned wholesale by `selection` (so a live choice of a kWh
 * with no tier cannot leave a stale `?tier=` stuck to it).
 *
 * A selection with no valid kWh returns `path` untouched — there is nothing to say,
 * and blanking a param the caller did not ask about is not this function's business.
 */
export function withUsageSelection(path: string, selection: UsageSelection): string {
  const extra = usageSearchParams(selection);
  if (extra.toString() === '') return path;

  const [base = path, existing = ''] = path.split('?');
  const merged = new URLSearchParams(existing);
  merged.delete(USAGE_PARAM);
  merged.delete(TIER_PARAM);
  extra.forEach((value, key) => merged.set(key, value));
  return `${base}?${merged.toString()}`;
}

/**
 * The shelf path carrying the shopper's usage selection, so a refresh (or a
 * bookmark, or a shared link) lands on the same priced shelf.
 */
export function buildShelfPath(selection: UsageSelection): string {
  return withUsageSelection('/shop/plans', selection);
}

/** `/plan/<tariffId>`, encoded — the base every plan-scoped route hangs off. */
function planBasePath(tariffId: string): string {
  return `/plan/${encodeURIComponent(tariffId)}`;
}

/**
 * The plan-detail path for a tariff, carrying the usage selection.
 *
 * This is what closes the reported defect: the shelf headlines a plan at the
 * shopper's chosen kWh, and the detail page it links to must headline the SAME
 * number — so the kWh travels on the link rather than being re-defaulted to the
 * §25.475 midpoint on arrival.
 */
export function buildPlanDetailPath(tariffId: string, selection: UsageSelection): string {
  return withUsageSelection(planBasePath(tariffId), selection);
}

/** The funnel steps that hang off a plan (`/plan/:slug/<step>`). */
export type PlanFunnelStep = 'credit' | 'estimate' | 'sign';

export interface PlanStepPathArgs {
  readonly tariffId: string;
  readonly step: PlanFunnelStep;
  readonly selection: UsageSelection;
  /**
   * The back-compat `?candidate=` handle, when the caller has one. Omitted links
   * still work — the steps read the candidate from `FunnelProvider` first
   * (`useFunnelCandidateId`).
   */
  readonly candidateId?: string;
}

/**
 * A funnel step path with BOTH query handles the step may need: the back-compat
 * `?candidate=` and the usage selection.
 *
 * Exists so no `.tsx` assembles a query string. Before this, each step page built
 * its own `?candidate=…` by hand and none of them carried the usage — which is why
 * a refresh at step 2/3/4, or Back out of one, forgot the shopper's kWh.
 */
export function buildPlanStepPath({
  tariffId,
  step,
  selection,
  candidateId,
}: PlanStepPathArgs): string {
  const base = `${planBasePath(tariffId)}/${step}`;
  const path =
    candidateId === undefined
      ? base
      : `${base}?${new URLSearchParams({ candidate: candidateId }).toString()}`;
  return withUsageSelection(path, selection);
}

export interface FunnelSeedArgs {
  /** The `GET /bff/funnel` body, or undefined when the query 401'd / failed. */
  readonly snapshot: FunnelSnapshotResponse | undefined;
  /** The current URL's query string. */
  readonly params: URLSearchParams;
  /**
   * Take the METER from the snapshot but NOT the server step. Default `false`.
   *
   * ⚠️ THIS IS NOT A TUNING KNOB — IT EXISTS BECAUSE THE TWO VALUES AGE
   * DIFFERENTLY, AND MIXING THEM STRANDS A CUSTOMER MID-FUNNEL.
   *
   * `serverStep` is read ONCE, at mount, and `FunnelProvider` holds it in
   * `useState` for the life of the page — nothing refetches it as the shopper
   * advances. `FunnelStepGate` then refuses any step beyond it
   * (`funnelGating.ts`: an `undefined` serverStep gates NOTHING, a stale one
   * gates everything past it).
   *
   * On an offer arrival the snapshot is read for the meter, and at that instant
   * the server step is `meter` — the customer has done nothing yet. Carry that
   * into the provider and the gate bounces them back to the credit step the
   * moment they click through to the estimate, forever. Observed 2026-09-18:
   * credit completed, deposit waiver recorded, and "See your estimated bill"
   * landed back on "Verify your identity" every time.
   *
   * The meter does not have that problem: it is a fact about the premise and
   * cannot go stale within a page load.
   */
  readonly meterOnly?: boolean;
}

/**
 * The initial funnel state for a returning tab: server-held meter + URL-held usage.
 *
 * ── AN ABSENT SNAPSHOT IS A VALID, EMPTY SEED ──────────────────────────────
 * `snapshot === undefined` covers BOTH "no funnel session (401)" and "the read
 * failed". Neither is treated as an error here, because there is nothing useful to
 * do differently: the shopper starts at the meter chooser either way, and the
 * URL-held usage is still restored (it costs nothing and helps if the session comes
 * back). Anything louder would turn a first-time visit into an error screen.
 *
 * ── THE ZIP MIRRORS THE METER, EXACTLY AS `setResolved` DOES ────────────────
 * So the summary card always renders a consistent ZIP/address pair regardless of
 * on-ramp — the ESI ID path never typed a ZIP at all.
 */
export function buildFunnelSeed({
  snapshot,
  params,
  meterOnly = false,
}: FunnelSeedArgs): FunnelSeed {
  const usage = parseUsageSelection(params);
  const candidate = snapshot?.candidate ?? null;
  // The server step rides EVEN WITH NO CANDIDATE: a session at step `meter` is
  // exactly the case the gate exists to catch, and returning early without it
  // would leave the deepest-linked shopper ungated.
  // `meterOnly` withholds the step deliberately — see `FunnelSeedArgs.meterOnly`
  // for why carrying a mount-time step into a live funnel strands the shopper.
  const step = snapshot === undefined || meterOnly ? {} : { serverStep: snapshot.step };
  // Names are nullable individually; a lead exists when the session holds one.
  // No snapshot → no claim either way (same absence rule as `serverStep`).
  const lead =
    snapshot === undefined ? {} : { hasLeadContact: snapshot.identity?.firstName != null };

  const resumed =
    snapshot?.resumed === true
      ? {
          resumed: true,
          ...(snapshot.resumeScope === null ? {} : { resumeScope: snapshot.resumeScope }),
        }
      : {};

  if (candidate === null) return { ...usage, ...step, ...lead, ...resumed };

  return {
    ...usage,
    ...step,
    ...lead,
    ...resumed,
    candidateId: candidate.candidateId,
    meterDetail: candidate.meter,
    zip: candidate.meter.zip,
  };
}
